<template>
  <div class="users-list">
    <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />
    <template v-if="isMobileDevice">
      <div v-if="items.length">
        <CustomersCard
          v-for="item of items"
          :id="item.id"
          :key="item.id"
          :source="item.source"
          :campaign="item.campaign"
          :medium="item.medium"
          :status="item.status"
          :company-title="item.companyData?.name"
          :company-code="item.companyData?.code"
          :company-type="item.companyData?.type"
          :tax-number="item.companyData?.taxNumber"
          :employee="item.userData"
          :processing-manager="item.processingManager"
          :contacts="
            item.userData ? { phone: item.userData.phone, email: item.userData.email } : undefined
          "
          :region="item.region"
          @clickOnOrder="onClickRow({ id: item.id, status: item.status })"
        />
      </div>
      <SkeletonLoaderCard v-else :table-headers="tableHeaders" />
    </template>

    <OurTable
      v-else
      :headers="headers.length ? headers : tableHeaders"
      :items="items"
      :table-fixed="false"
      class="users-list-table"
      @clickRow="onClickRow"
    >
      <template #cell-companyData="{ value }">
        <div class="flex flex-col gap-1">
          <div class="text">
            <span class="text-gray-500">{{ $t("label.companyTitle") }}:</span>
            {{ value.name }}
          </div>
          <div v-if="value.code" class="text">
            <span class="text-gray-500">
              {{
                value.code
                  ? value.type === "juridicalPerson"
                    ? $t("label.taxNumber")
                    : $t("label.personalTaxNumber")
                  : ""
              }}:</span
            >
            {{ value.code }}
          </div>
          <div v-if="value.taxNumber" class="text">
            <span class="text-gray-500">
              {{ value.taxNumber ? $t("label.personalTaxNumber") : "" }}:</span
            >
            {{ value.taxNumber }}
          </div>
        </div>
      </template>

      <template #cell-userData="{ value }">
        <div class="contacts-block flex flex-col gap-1">
          <div>{{ value.primaryRow }}</div>
          <div class="text-xs text-gray-500">{{ value.secondaryRow }}</div>
          <div>{{ value.phone }}</div>
          <OurLink
            class="email"
            :text="value.email"
            :url="value.email"
            type="email"
            size="xs"
            color="gray"
            dashed
            no-focus-ring
            @click.native.stop
          />
        </div>
      </template>

      <template #cell-source="{ value }">
        <OurTag v-if="value" size="md" :text="value?.text" :color="value?.color" />
        <div v-else>–</div>
      </template>

      <template #cell-status="{ value }">
        <OurTag size="md" :text="value?.text" :color="value?.color" />
      </template>
    </OurTable>

    <OurPagination
      v-if="isMoreThanOnePage"
      :current-page="paginationData?.currentPage"
      :per-page="paginationData?.perPage"
      :total="paginationData?.total"
      @pageChange="onPageChange"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import MainSubMenu from "@/layouts/_bit/admin-mono/MainSubMenu.vue";
import CustomersCard from "@/components/MobileCards/CustomersCard.vue";
import SkeletonLoaderCard from "@/ui/loader/SkeletonLoaderCard.vue";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";
export default {
  name: "DealershipApplicationsList",
  components: { CustomersCard, MainSubMenu, SkeletonLoaderCard, TotalInfoBlock },

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    headers: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      isShownMenuBlock: null,
    };
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["isUserDealer", "getPermission"]),
    icons: () => ({
      more: require("@material-symbols/svg-400/outlined/more_horiz-fill.svg"),
    }),
    tableHeaders() {
      const data = [
        {
          value: "id",
          text: this.$t("page.users.id"),
          thClass: "w-24",
          tdClass: "max-w-[2rem] min-w-[5rem]",
        },
        {
          value: "createdAt",
          text: this.$t("label.created"),
          thClass: "w-[5rem]",
          tdClass: "max-w-[8rem] min-w-[8rem]",
        },
        {
          value: "companyData",
          text: this.$t("label.companyData"),
          thClass: "w-[10rem]",
          tdClass: "max-w-[10rem] min-w-[10rem]",
        },
        {
          value: "userData",
          text: this.$t("label.userData"),
          thClass: "w-[5rem]",
          tdClass: "max-w-[10rem] min-w-[10rem]",
        },
        {
          value: "region",
          text: this.$t("label.region"),
          thClass: "w-[5rem]",
          tdClass: "max-w-[9rem] min-w-[7rem]",
        },
        {
          value: "processingManager",
          text: this.$t("label.manager"),
          thClass: "w-[5rem]",
          tdClass: "max-w-[9rem] min-w-[7rem]",
        },
        ...(this.getPermission["customer.applications_utm"]
          ? [
              {
                value: "campaign",
                text: this.$t("label.campaign"),
                thClass: "w-[5rem]",
                tdClass: "max-w-[12rem] min-w-[9rem]",
              },
              {
                value: "medium",
                text: this.$t("label.medium"),
                thClass: "w-[5rem]",
                tdClass: "max-w-[12rem] min-w-[9rem]",
              },
              {
                value: "source",
                text: this.$t("label.source"),
                thClass: "w-[5rem]",
                tdClass: "max-w-[12rem] min-w-[9rem]",
              },
            ]
          : []),
        {
          value: "status",
          text: this.$t("page.fleaMarket.status"),
          thClass: "w-[5rem]",
          tdClass: "max-w-[13rem] min-w-[7rem]",
        },
      ];

      return data;
    },

    isMoreThanOnePage() {
      return this.paginationData?.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },
  },

  watch: {
    routePage(newPage) {
      if (newPage !== this.paginationData?.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onClickRow(row) {
      this.$emit("onClickRow", row.id);
    },
  },
};
</script>

<style lang="postcss" scoped>
.users-list {
  .contacts-block,
  .company-block {
    @apply overflow-hidden;

    .email,
    .text {
      @apply truncate;
    }
  }
}
</style>
